import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import PresentationSlide from '../components/PresentationSlide'
import EnhancedNavigation from '../components/EnhancedNavigation'
import EnhancedSlideIndicator from '../components/EnhancedSlideIndicator'
import ParticleBackground from '../components/ParticleBackground'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'
import { useSwipe } from '../hooks/useSwipe'
import { GeneratedSlide } from '../types/generator'
import { Slide } from '../types/slide'

function Presentation() {
  const navigate = useNavigate()
  const [slides, setSlides] = useState<Slide[]>([])
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    // Load generated slides from sessionStorage
    const stored = sessionStorage.getItem('generatedSlides')
    if (stored) {
      try {
        const generatedSlides: GeneratedSlide[] = JSON.parse(stored)
        // Convert GeneratedSlide to Slide (they're compatible)
        setSlides(generatedSlides as Slide[])
      } catch (error) {
        console.error('Failed to parse generated slides:', error)
        navigate('/')
      }
    } else {
      // No slides found, redirect to home
      navigate('/')
    }
  }, [navigate])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault()
          setCurrentSlide((prev) => (prev + 1) % slides.length)
          break
        case 'ArrowLeft':
          e.preventDefault()
          setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
          break
        case 'f':
        case 'F11':
          e.preventDefault()
          toggleFullscreen()
          break
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [isFullscreen, slides.length])

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      await document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const exitFullscreen = async () => {
    await document.exitFullscreen()
    setIsFullscreen(false)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const goHome = () => {
    navigate('/')
  }

  // Swipe functionality for mobile
  const swipeHandlers = useSwipe({
    onSwipeLeft: nextSlide,
    onSwipeRight: prevSlide,
    threshold: 50
  })

  if (slides.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Loading presentation...</p>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
      {...swipeHandlers}
    >
      <Navigation />
      <ParticleBackground />
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <PresentationSlide 
            slide={slides[currentSlide]} 
            slideNumber={currentSlide + 1}
            totalSlides={slides.length}
          />
        </motion.div>
      </AnimatePresence>

      {/* Enhanced Navigation System */}
      <EnhancedNavigation
        currentSlide={currentSlide}
        totalSlides={slides.length}
        onNext={nextSlide}
        onPrev={prevSlide}
        onGoToSlide={goToSlide}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onGoHome={goHome}
      />

      <EnhancedSlideIndicator
        currentSlide={currentSlide}
        totalSlides={slides.length}
        onGoToSlide={goToSlide}
        slides={slides}
      />

      <Footer />
    </div>
  )
}

export default Presentation

